import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    templateType: '',
    overdueOnly: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardData, assignmentsData] = await Promise.all([
        api.get('/reporting/admin-dashboard'),
        api.get('/reporting/assignments'),
      ]);
      setStats(dashboardData);
      setAssignments(assignmentsData.assignments);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.templateType) params.append('template_type', filters.templateType);
      if (filters.overdueOnly) params.append('overdue_only', 'true');
      
      const data = await api.get(`/reporting/assignments?${params.toString()}`);
      setAssignments(data.assignments);
    } catch (err) {
      console.error('Failed to filter:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const data = await api.get('/reporting/export-csv');
      const blob = new Blob([data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export CSV: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'warning';
      case 'OVERDUE': return 'danger';
      default: return 'neutral';
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (!stats) {
    return <div>Failed to load dashboard</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <p className="page-subtitle">System-wide analytics and reporting</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>Total Users</div>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--primary-blue-700)' }}>
            {stats.summary.total_users}
          </div>
        </div>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>Total Properties</div>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--primary-blue-700)' }}>
            {stats.summary.total_properties}
          </div>
        </div>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>Active Templates</div>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--primary-blue-700)' }}>
            {stats.summary.active_templates}
          </div>
        </div>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>Total Assignments</div>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--primary-blue-700)' }}>
            {stats.summary.total_assignments}
          </div>
        </div>
      </div>

      {/* Assignment Stats */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h2 className="card-title">Assignment Statistics</h2>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Overall Completion Rate</span>
              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-600)' }}>
                {stats.summary.completion_rate}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '12px',
              backgroundColor: 'var(--gray-200)',
              borderRadius: '6px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${stats.summary.completion_rate}%`,
                height: '100%',
                backgroundColor: 'var(--success-600)',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-600)' }}>
                {stats.assignment_stats.completed}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Completed</div>
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--warning-600)' }}>
                {stats.assignment_stats.in_progress}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>In Progress</div>
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--danger-600)' }}>
                {stats.assignment_stats.overdue}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Overdue</div>
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--gray-600)' }}>
                {stats.assignment_stats.not_started}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Not Started</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h2 className="card-title">Filters</h2>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={handleFilter} data-testid="apply-filters">
              Apply Filters
            </button>
            <button className="btn btn-primary" onClick={handleExportCSV} data-testid="export-csv">
              📥 Export CSV
            </button>
          </div>
        </div>
        <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              <option value="NOT_STARTED">Not Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="OVERDUE">Overdue</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Template Type</label>
            <select
              className="form-select"
              value={filters.templateType}
              onChange={(e) => setFilters({ ...filters, templateType: e.target.value })}
              data-testid="filter-type"
            >
              <option value="">All Types</option>
              <option value="ONBOARDING">Onboarding</option>
              <option value="OPERATIONS">Operations</option>
              <option value="AUDIT">Audit</option>
              <option value="CUSTOM">Custom</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={filters.overdueOnly}
                onChange={(e) => setFilters({ ...filters, overdueOnly: e.target.checked })}
                style={{ marginRight: '0.5rem' }}
                data-testid="filter-overdue"
              />
              <span className="form-label" style={{ marginBottom: 0 }}>Overdue Only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Assignments ({assignments.length})</h2>
        </div>
        {assignments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <h3 className="empty-state-title">No Assignments Found</h3>
            <p className="empty-state-description">Try adjusting your filters or create some assignments.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Template</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>Target Date</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((item) => (
                  <tr key={item.assignment.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{item.user?.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>{item.user?.email}</div>
                    </td>
                    <td>{item.template?.name}</td>
                    <td>
                      <span className="badge badge-info">{item.template?.type}</span>
                    </td>
                    <td>
                      <span className={`badge badge-${getStatusColor(item.assignment.status)}`}>
                        {item.assignment.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{new Date(item.assignment.startDate).toLocaleDateString()}</td>
                    <td>
                      {item.assignment.targetCompletionDate
                        ? new Date(item.assignment.targetCompletionDate).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
